import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGeneral } from '../contexts';
import { subscribeAuthMessage, AuthMessage } from '../utils/authChannel';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, language } = useGeneral();
  const { isAuthenticated, isLoading } = auth;
  const { getTranslation } = language;
  
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const hasNavigatedRef = useRef(false);
  const navigationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Listen to logout broadcasts
  useEffect(() => {
    const unsubscribe = subscribeAuthMessage((message: AuthMessage) => {
      if (message.type === 'LOGOUT') {
        setIsLoggingOut(true);
        hasNavigatedRef.current = false;
      }
    });
    return unsubscribe;
  }, []);

  // Handle navigation when not authenticated
  useEffect(() => {
    // Reset navigation flag when auth state changes
    if (isAuthenticated) {
      hasNavigatedRef.current = false;
      return;
    }

    // Don't navigate if already navigating or still loading
    if (isLoading || hasNavigatedRef.current || isLoggingOut) {
      return;
    }

    // Navigate to login with replace
    if (!isAuthenticated) {
      hasNavigatedRef.current = true;
      // Small delay to allow cleanup
      navigationTimerRef.current = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 50);
    }

    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
      }
    };
  }, [isAuthenticated, isLoading, navigate, isLoggingOut]);

  // Reset navigation flag when location changes
  useEffect(() => {
    hasNavigatedRef.current = false;
  }, [location.pathname]);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div>{getTranslation('common.loading')}</div>
      </div>
    );
  }

  // Return null immediately if logging out or not authenticated
  if (isLoggingOut || !isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

